import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import * as endpoint from "./../../../Service/endpoints";
import * as service from "./../../../Service/service";
import MainSidebar from "./MainSidebar";
import Icon from "../../UI/Icon";
import Modal from "../../UI/Modal";
import { Logo } from "../../UI/Primitives";
import SearchBar from "../../Layout/SearchBar";
import ThemeToggle from "../../Layout/ThemeToggle";
import BottomNav from "../../Layout/BottomNav";
import loadable from "../../Layout/loadable";
import { useCartCount } from "../../../Service/cartCount";
import { NAV_CATEGORIES, getCategoryMeta } from "../../User/Catalog/categoryMeta";
import "../../Layout/layout.css";

const LIST_ITEM_URL = "https://p2p.voorent.com/";

// The login modal (and its form/validation dependencies, ~65 KB gzipped) loads
// on first use only — most visitors never open it.
const LoginWrapper = loadable(() => import(/* webpackChunkName: "login-modal" */ "./../../User/Login/LoginWrapper"));

// Legacy pages have a <main> without the #main-content id; fall back to it.
function skipToContent(event) {
  const target = document.getElementById("main-content") || document.querySelector("main");
  if (!target) return;
  event.preventDefault();
  if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
  target.focus();
  target.scrollIntoView();
}

function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setScrolled(window.scrollY > threshold);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [threshold]);
  return scrolled;
}

function HeaderComponent() {
  const dispatch = useDispatch();
  const location = useLocation();
  const userState = useSelector((state) => state.userState);
  const scrolled = useScrolled();
  const [searchOpen, setSearchOpen] = useState(false);
  const cartCount = useCartCount(userState.loggedIn);
  const [loginRequested, setLoginRequested] = useState(userState.showLogin);

  const city = endpoint.getCurrentCity();
  const cityLabel = city.charAt(0).toUpperCase() + city.slice(1);
  const segments = location.pathname.split("/").filter(Boolean);
  const activeCategory = segments.length >= 2 && segments[0] === city ? endpoint.apiCategorySlug(segments[1]) : null;
  const isHome = segments.length === 0 || (segments.length === 1 && segments[0] === city);

  // Restore the signed-in session (unchanged behaviour); user info is fetched once.
  useEffect(() => {
    if (typeof localStorage.keycloak !== "undefined") {
      dispatch({ type: "user.auth.login", data: JSON.parse(localStorage.keycloak) });
      if (!userState.userInfo || !Object.keys(userState.userInfo).length) {
        service.getUserInfo().then(
          (response) => dispatch({ type: "login.update.userInfo", data: response.data }),
          () => {}
        );
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (userState.showLogin) setLoginRequested(true);
  }, [userState.showLogin]);

  const openLogin = () => dispatch({ type: "login.window.show" });
  const openCity = () => dispatch({ type: "user.city.show" });
  const openMenu = () => dispatch({ type: "user.menu.show" });

  const navItems = NAV_CATEGORIES.map((slug) => ({
    slug,
    label: getCategoryMeta(slug).short,
    to: endpoint.getPath(slug),
  }));
  if (city === "gurgaon") {
    navItems.push({ slug: "expressDelivery", label: "Express", to: endpoint.getPath("expressDelivery") });
  }

  const firstName = userState.userInfo && userState.userInfo.firstName;

  return (
    <>
      <a href="#main-content" className="v-skip-link" onClick={skipToContent}>
        Skip to content
      </a>
      <div className="v-header-spacer" aria-hidden="true" />
      <header className={`v-header ${scrolled ? "is-scrolled" : ""}`}>
        <div className="v-header__inner">
          <button type="button" className="v-icon-btn v-header__menu" onClick={openMenu} aria-label="Open menu">
            <Icon name="menu" />
          </button>

          <Link to={endpoint.getPath("")} className="v-header__brand" aria-label="Voorent home">
            <Logo height={24} />
          </Link>

          <nav className="v-header__nav" aria-label="Primary">
            <Link to={endpoint.getPath("")} className="v-nav-link" aria-current={isHome ? "page" : undefined}>
              Home
            </Link>
            {navItems.map((item) => (
              <Link
                key={item.slug}
                to={item.to}
                className="v-nav-link"
                aria-current={activeCategory === item.slug ? "page" : undefined}
              >
                {item.label}
              </Link>
            ))}
            <a href={LIST_ITEM_URL} className="v-nav-link v-nav-link--buy">
              List Item
            </a>
          </nav>

          <div className="v-header__search">
            <SearchBar variant="header" />
          </div>

          <div className="v-header__actions">
            <button
              type="button"
              className="v-icon-btn v-header__search-btn"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <Icon name="search" />
            </button>

            <button type="button" className="v-location" onClick={openCity} aria-label={`Delivery city: ${cityLabel}. Change city`}>
              <Icon name="pin" size={16} />
              <span>{cityLabel}</span>
              <Icon name="chevronDown" size={14} className="v-location__chev" />
            </button>

            <div className="v-header__theme">
              <ThemeToggle />
            </div>

            <a href="/cart" className="v-icon-btn v-header__cart" aria-label={cartCount ? `Cart, ${cartCount} items` : "Cart"}>
              <Icon name="bag" />
              {cartCount ? <span className="v-count">{cartCount > 9 ? "9+" : cartCount}</span> : null}
            </a>

            {userState.loggedIn ? (
              <a href="/account" className="v-account" aria-label="My account">
                <span className="v-account__avatar" aria-hidden="true">
                  {firstName ? firstName.charAt(0).toUpperCase() : <Icon name="user" size={16} />}
                </span>
                <span className="v-account__label">{firstName || "Account"}</span>
              </a>
            ) : (
              <button type="button" className="v-account" onClick={openLogin}>
                <span className="v-account__avatar" aria-hidden="true">
                  <Icon name="user" size={16} />
                </span>
                <span className="v-account__label">Sign in</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <Modal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Search Voorent"
        hideTitle
        closeButton
        variant="search"
      >
        <SearchBar variant="panel" autoFocus onNavigate={() => setSearchOpen(false)} />
      </Modal>

      <MainSidebar />
      <BottomNav onSearch={() => setSearchOpen(true)} cartCount={cartCount} />
      {loginRequested ? <LoginWrapper /> : null}
    </>
  );
}

export default HeaderComponent;
